import PropTypes from 'prop-types';
import { always } from 'ramda';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
dayjs.extend(duration);
dayjs.extend(utc);

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Container, Typography } from '@mui/material';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import Navigation from '../Navigation';
import { webPaths } from '../../routes/paths';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { SearchFilters } from '@classity-edtech/ui-base/dist/components/Displays/SearchFilters/SearchFilters';
import { Reservation } from '@classity-edtech/ui-base/dist/components/Displays/Reservation/Reservation';
import {
  AlertComp,
  ArrowLeftIcon,
  BasketIcon,
  Cancel,
  Divider,
  PeopleIcon,
  Sheet,
  UserProfile,
} from '@classity-edtech/ui-base/dist/components/Displays';
import {
  CountryCode,
  CredentialCategory,
  CurrencyCode,
  LanguageCode,
  NotificationLabel,
  OrderFullfillmentStatus,
} from '@classity-edtech/platform-utils';
import { Stack } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  formatError,
  generateImageAssestUrl,
  getCountry,
  windowExists,
} from '@classity-edtech/ui-base';
import Draggable from 'react-draggable';
import { useProductEventsQuery } from '../../services/productEvents';
import { ProductEvent } from '../../services/models/productEvents';
import { User } from '../../services/models/authentication';
import { useLanguagesQuery } from '../../services/languages';
import { useCreateOrderMutation, useMyOrdersQuery } from '../../services/payment';
import { updateFlashMessage } from '../../store/actions/flashMessage';
import { useAppDispatch } from '../../store/hooks';
import { Price } from '../../services/models/product';
import { refinePricesUtil } from '../../utils/refinePrices';
import { useLazyProductEventsQuery } from '../../services/productEvents';
import { useGetMyProfileQuery } from '../../services/users';

type Booking = {
  product: ProductEvent;
  price: Price;
};

type availability = 'available' | 'unavailable';

export interface TuitionProps extends MuiBoxProps {}

export const BookTuition = ({ className }: TuitionProps) => {
  const dispatch: any = useAppDispatch();
  const defaultLanguage = LanguageCode.EN;
  const defaultCurrency = CurrencyCode.NG;
  const screens = useAppMediaQuery();
  const navigate = useNavigate();
  const [defaultPos, setDefaultPos] = useState({ x: 0, y: 0 });
  const [groupFilters, setGroupFilters] = useState({
    groupOf3: false,
    groupOf6: false,
  });
  const [loadingClient, setLoadingClient] = useState(true);
  const [timeFilters, setTimeFilters] = useState({
    earlyMorning: false,
    midMorning: false,
    lateMorning: false,
    earlyAfternoon: false,
    midAfternoon: false,
    lateAfternoon: false,
  });
  const [availability, setAvailability] = useState<availability | ''>('');
  const [locationFilters, setLocationFilters] = useState({
    chevron: false,
  });
  const [openMobileSummary, setOpenMobileSummary] = useState(false);
  const [createOrder] = useCreateOrderMutation();
  const [getProductEvent] = useLazyProductEventsQuery();
  const { data: myOrders, isLoading: loadingMyOrders } = useMyOrdersQuery({
    page: 1,
    limit: 1,
    status: OrderFullfillmentStatus?.PLACED,
  });

  const { data: user } = useGetMyProfileQuery();

  const toLocalISOString = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60000;
    const localISO = new Date(date.getTime() - offset).toISOString().slice(0, -1);
    return localISO;
  };

  const [filteredDate, setFilteredDate] = useState(toLocalISOString(new Date()));

  const [previewTutor, setPreviewTutor] = useState<User | undefined>();

  const [bookings, setBookings] = useState<{
    groupof3: Booking | undefined;
    groupof6: Booking | undefined;
  }>({
    groupof3: undefined,
    groupof6: undefined,
  });

  const [previewBooking, setPreviewBooking] = useState<ProductEvent | undefined>();

  const handlePreviewBooking = (product: ProductEvent) => {
    setPreviewBooking(product);
  };

  const closePreviewBooking = () => {
    setPreviewBooking(undefined);
  };

  const handleRemoveBooking = (productId: string) => {
    if (bookings.groupof3?.product?.id === productId) {
      setBookings(booking => ({ ...booking, groupof3: undefined }));
    }
    if (bookings.groupof6?.product?.id === productId) {
      setBookings(booking => ({ ...booking, groupof6: undefined }));
    }

    closePreviewBooking();
  };

  const bookingTotal =
    (bookings?.groupof3?.price?.currencies?.find(c => c?.code === defaultCurrency)?.amount || 0) +
    (bookings?.groupof6?.price?.currencies?.find(c => c?.code === defaultCurrency)?.amount || 0);

  const addBooking = ({
    e,
    product,
    price,
  }: {
    e: React.FormEvent<EventTarget>;
    product: ProductEvent;
    price: Price;
  }) => {
    const target = e.target as HTMLInputElement;

    if (target.checked) {
      const group = product?.maxAttendees < 5 ? 'groupof3' : 'groupof6';

      setBookings(value => {
        const booking = {
          ...value,
          [group]: { product, price },
        };

        const prices = Object.values(booking)
          ?.map(b => {
            if (b?.price) {
              return {
                productId: b?.price.productId,
                id: b?.price.id,
                currencies: b?.price.currencies,
                split: b?.price.split,
              };
            }
          })
          ?.filter(Boolean);
        const products = Object.values(booking)
          ?.map(b => {
            if (b?.product) {
              const organizer = b?.product?.attendees?.find(a => a.organizer)?.user;
              return {
                attendees: b?.product?.attendees?.map(a => ({
                  ...a,
                  user: undefined,
                })),
                calendarId: b?.product.calendarId,
                description: b?.product.description,
                endTime: b?.product.endTime,
                id: b?.product.id,
                code: b?.product.code,
                location: b?.product.location,
                maxAttendees: b?.product.maxAttendees,
                category: b?.product.category,
                name: b?.product.name,
                startTime: b?.product.startTime,
                isVirtual: b?.product.isVirtual,
                organizer: {
                  uid: organizer?.profile?.ids?.find(id => id?.name === 'uid')?.value,
                  email: organizer?.email,
                  userId: organizer?.id,
                  name: `${organizer?.profile?.firstname} ${organizer?.profile?.lastname}`,
                },
                recurringEventId: b?.product.recurringEventId,
              };
            }
          })
          ?.filter(Boolean) as any;

        handleOrderCreation({ prices, products });

        return {
          ...value,
          [group]: { product, price },
        };
      });
    }
  };

  const pills = [
    groupFilters.groupOf3 && { label: 'Group of 3', value: 'groupOf3', type: 'group' },
    groupFilters.groupOf6 && { label: 'Group of 6', value: 'groupOf6', type: 'group' },
    timeFilters.earlyMorning && { label: 'Early morning', value: 'earlyMorning', type: 'time' },
    timeFilters.midMorning && { label: 'Mid morning', value: 'midMorning', type: 'time' },
    timeFilters.lateMorning && { label: 'Late morning', value: 'lateMorning', type: 'time' },
    timeFilters.earlyAfternoon && {
      label: 'Early afternoon',
      value: 'earlyAfternoon',
      type: 'time',
    },
    timeFilters.midAfternoon && { label: 'Mid afternoon', value: 'midAfternoon', type: 'time' },
    timeFilters.lateAfternoon && { label: 'Late afternoon', value: 'lateAfternoon', type: 'time' },
    locationFilters.chevron && { label: 'Chevron', value: 'chevron', type: 'location' },
    availability === 'available' && {
      label: 'Available',
      value: 'available',
      type: 'availability',
    },
    availability === 'unavailable' && {
      label: 'Unavailable',
      value: 'unavailable',
      type: 'availability',
    },
  ]?.filter(Boolean);

  const adjustTimeOfDate = () => {
    const date = filteredDate?.split('T')[0];

    if (timeFilters.earlyMorning) {
      return {
        earliest: `${date}T06:00:00.000+00:00`,
        latest: `${date}T08:00:00.000+00:00`,
      };
    }
    if (timeFilters.midMorning) {
      return {
        earliest: `${date}T08:00:00.000+00:00`,
        latest: `${date}T10:00:00.000+00:00`,
      };
    }
    if (timeFilters.lateMorning) {
      return {
        earliest: `${date}T10:00:00.000+00:00`,
        latest: `${date}T12:00:00.000+00:00`,
      };
    }
    if (timeFilters.earlyAfternoon) {
      return {
        earliest: `${date}T12:00:00.000+00:00`,
        latest: `${date}T14:00:00.000+00:00`,
      };
    }
    if (timeFilters.midAfternoon) {
      return {
        earliest: `${date}T14:00:00.000+00:00`,
        latest: `${date}T16:00:00.000+00:00`,
      };
    }
    if (timeFilters.lateAfternoon) {
      return {
        earliest: `${date}T16:00:00.000+00:00`,
        latest: `${date}T18:00:00.000+00:00`,
      };
    }

    return {
      earliest: `${date}T06:00:00.000+00:00`,
      latest: `${date}T18:00:00.000+00:00`,
    };
  };

  const getGroupSize = () => {
    if (groupFilters.groupOf3 && groupFilters.groupOf6) {
      return { mingroup: 1, maxgroup: 7 };
    }
    if (groupFilters.groupOf3) {
      return { mingroup: 1, maxgroup: 4 };
    }
    if (groupFilters.groupOf6) {
      return { mingroup: 5, maxgroup: 7 };
    }
    return { mingroup: 1, maxgroup: 7 };
  };

  const queries = {
    page: 1,
    limit: 50,
    ...adjustTimeOfDate(),
    ...getGroupSize(),
    location: locationFilters.chevron ? 'chevron' : undefined,
  };

  const {
    data: productEvents,
    isLoading: loadingProductEvents,
    isFetching: fetchingProductEvents,
    isError: isProductEventsError,
  } = useProductEventsQuery(queries);

  const refinedProductEvents = availability
    ? productEvents?.filter(p => {
        if (availability === 'available') {
          return p?.maxAttendees - 1 > p?.lessons?.[0]?.participants?.length;
        } else {
          return p?.maxAttendees - 1 <= p?.lessons?.[0]?.participants?.length;
        }
      })
    : productEvents;

  const { data: languages } = useLanguagesQuery();

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: defaultCurrency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const handleClosePill = ({ value, type }: { label: string; value: string; type: string }) => {
    if (type === 'time') {
      setTimeFilters(filters => ({ ...filters, [value]: false }));
    }
    if (type === 'location') {
      setLocationFilters(filters => ({ ...filters, [value]: false }));
    }
    if (type === 'availability') {
      setAvailability('');
    }
    if (type === 'group') {
      setGroupFilters(filters => ({ ...filters, [value]: false }));
    }
  };

  const goBack = async () => {
    navigate(`/${webPaths.studentTuition}`);
  };

  const clearGroupFilter = () => {
    setGroupFilters({
      groupOf3: false,
      groupOf6: false,
    });
  };

  const clearTimeFilter = () => {
    setTimeFilters({
      earlyMorning: false,
      midMorning: false,
      lateMorning: false,
      earlyAfternoon: false,
      midAfternoon: false,
      lateAfternoon: false,
    });
  };

  const clearLocationFilter = () => {
    setLocationFilters({
      chevron: false,
    });
  };

  const handleAllGroupFilterSelection = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;

    if (!target.checked) {
      return clearGroupFilter();
    }

    setGroupFilters({
      groupOf3: true,
      groupOf6: true,
    });
  };

  const toggleMobileSummary = () => {
    if (bookingTotal > 0) {
      setOpenMobileSummary(value => !value);
    }
  };

  const handleGroupFilterChange = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;

    setGroupFilters(filters => ({ ...filters, [target.name]: target.checked }));
  };

  const handleTimeFilterChange = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;

    if (target.checked) {
      clearTimeFilter();
    }

    setTimeFilters(filters => ({ ...filters, [target.name]: target.checked }));
  };

  const handleLocationFilterChange = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;

    if (target.checked) {
      clearLocationFilter();
    }

    setLocationFilters(filters => ({ ...filters, [target.name]: target.checked }));
  };

  const handleAvailabilityFilterChange = (e: React.FormEvent<EventTarget>) => {
    const target = e.target as HTMLInputElement;

    setAvailability(target.name as availability);
  };

  const handlePreviousDate = () => {
    const newDate = new Date(filteredDate);
    newDate.setDate(newDate.getDate() - 1);

    if (dayjs(newDate).isBefore(dayjs(), 'day')) {
      return false;
    }

    return setFilteredDate(toLocalISOString(newDate));
  };

  const handleNextDate = () => {
    const newDate = new Date(filteredDate);
    newDate.setDate(newDate.getDate() + 1);

    return setFilteredDate(toLocalISOString(newDate));
  };

  useEffect(() => {
    setDefaultPos({
      x: (windowExists as any).innerWidth - 90,
      y: (windowExists as any).innerHeight / 2 - 72,
    });
  }, [(windowExists as any).innerWidth]);

  const openTutorPreview = (tutor: User) => {
    setPreviewTutor(tutor);
  };

  const closeTutorPreview = () => {
    setPreviewTutor(undefined);
  };

  const handleResetFilter = () => {
    clearLocationFilter();
    clearTimeFilter();
    clearGroupFilter();
  };

  const searchFiltersDetails = {
    filteredDate: {
      date: filteredDate,
      setDate: date => setFilteredDate(toLocalISOString(date)),
      previousDate: handlePreviousDate,
      nextDate: handleNextDate,
    },
    filterDropdowns: [
      {
        label: 'Group size',
        checkBoxes: [
          {
            label: '3 Students per Group (1 - 3)',
            name: 'groupOf3',
            checked: groupFilters.groupOf3,
            onChange: e => handleGroupFilterChange(e),
          },
          {
            label: '6 Students per Group (1 - 6)',
            name: 'groupOf6',
            checked: groupFilters.groupOf6,
            onChange: e => handleGroupFilterChange(e),
          },
        ],
        selectAllChange: e => handleAllGroupFilterSelection(e),
        closeAction: () => {},
        clearAction: clearGroupFilter,
      },
      {
        label: 'Availability',
        radios: [
          {
            label: 'All',
            name: '',
            checked: availability === '',
            onChange: e => handleAvailabilityFilterChange(e),
          },
          {
            label: 'Available',
            name: 'available',
            checked: availability === 'available',
            onChange: e => handleAvailabilityFilterChange(e),
          },
          {
            label: 'Unvailable',
            name: 'unavailable',
            checked: availability === 'unavailable',
            onChange: e => handleAvailabilityFilterChange(e),
          },
        ],
        closeAction: () => {},
      },
      {
        label: 'Time of Day',
        radios: [
          {
            label: 'Early morning (6-8am)',
            name: 'earlyMorning',
            checked: timeFilters.earlyMorning,
            onChange: e => handleTimeFilterChange(e),
          },
          {
            label: 'Mid morning (8-10am)',
            name: 'midMorning',
            checked: timeFilters.midMorning,
            onChange: e => handleTimeFilterChange(e),
          },
          {
            label: 'Late morning (10-12pm)',
            name: 'lateMorning',
            checked: timeFilters.lateMorning,
            onChange: e => handleTimeFilterChange(e),
          },
          {
            label: 'Early afternoon (12-2pm)',
            name: 'earlyAfternoon',
            checked: timeFilters.earlyAfternoon,
            onChange: e => handleTimeFilterChange(e),
          },
          {
            label: 'Mid afternoon (2-4pm)',
            name: 'midAfternoon',
            checked: timeFilters.midAfternoon,
            onChange: e => handleTimeFilterChange(e),
          },
          {
            label: 'Late afternoon (4-6pm)',
            name: 'lateAfternoon',
            checked: timeFilters.lateAfternoon,
            onChange: e => handleTimeFilterChange(e),
          },
        ],
        closeAction: () => {},
        clearAction: clearTimeFilter,
      },
      {
        label: 'Location',
        radios: [
          {
            label: 'Classity Tuition Lab @Chevron, Lekki',
            name: 'chevron',
            checked: locationFilters.chevron,
            onChange: e => handleLocationFilterChange(e),
          },
        ],
        closeAction: () => {},
        clearAction: clearLocationFilter,
      },
    ],
    ...(!loadingProductEvents &&
      !fetchingProductEvents &&
      (isProductEventsError || !refinedProductEvents?.length) && {
        notFound: {
          title: 'No matching results',
          description: 'Refine the search filter above or search for the next available classes',
          ctaLabel: 'See available classes',
          ctaAction: () => {},
        },
      }),
    closeSelectedFilterAction: handleClosePill,
    cancelFilterAction: handleResetFilter,
    selectedFilters: pills,
    resultSummary: `${isProductEventsError ? 0 : refinedProductEvents?.length || 0} ${refinedProductEvents?.length > 1 || refinedProductEvents?.length === 0 || isProductEventsError ? 'classes' : 'class'} found`,
  };

  const userProfile = () => {
    const profile = previewTutor?.profile;
    const credentials = previewTutor?.profile?.certifications;

    const getCertificationTitle = (type: CredentialCategory) => {
      switch (type) {
        case CredentialCategory.UNDERGRADUATE:
          return 'Undergraduate Certification';
        case CredentialCategory.POSTGRADUATE:
          return 'Postgraduate Certification';
        case CredentialCategory.DOCTORAL:
          return 'Doctoral Certification';
        case CredentialCategory.PROFESSIONAL:
          return 'Professional Certification';
        case CredentialCategory.DIPLOMA:
          return 'Diploma Certification';
        case CredentialCategory.TRAINING:
          return 'Training Certification';

        default:
          return 'Certification';
      }
    };

    return {
      profile: {
        name: `${profile?.firstname} ${profile?.lastname}`,
        src: generateImageAssestUrl(profile?.avatar?.contentUrl, 2),
        badge: 'Tutor',
        countryCode: previewTutor?.country || CountryCode.NG,
      },
      bio: profile?.bio,
      languages: previewTutor?.preferences?.knowsLanguages?.map(
        code => languages?.find(l => l?.code === code)?.name?.text,
      ),
      credentials: credentials?.map(c => ({
        title: getCertificationTitle(c?.category as CredentialCategory),
        institution: c?.issuedByName,
        date: getCountry(c?.countryOfOrigin),
      })),
      tabs: [],
    };
  };

  const formatDateEvent = (event: ProductEvent) => {
    const start = dayjs(event.startTime.dateTime);
    const end = dayjs(event.endTime.dateTime);

    const diff = dayjs.duration(end.diff(start));
    const hours = diff.hours();
    const minutes = diff.minutes();

    return {
      day: start.format('ddd'),
      date: start.format('DD'),
      time: `${start.format('HH:mm')} - ${end.format('HH:mm')}`,
      range:
        hours && minutes
          ? `${hours} hour${hours > 1 ? 's' : ''}, ${minutes} min`
          : hours
            ? `${hours} hour${hours > 1 ? 's' : ''}`
            : `${minutes} min`,
      month: start.format('MMM'),
    };
  };

  const reservationDetails = (event: ProductEvent) => {
    const dateExtract = formatDateEvent(event);
    const user = event?.attendees?.[0]?.user;
    const participantLength = event?.lessons?.[0]?.participants?.length || 0;
    const remainingSlots = event?.maxAttendees - 1 - participantLength;

    return {
      defaultOpen: true,
      appointmentDetails: {
        appointments: [
          {
            appointmentDay: dateExtract.day,
            appointmentDate: dateExtract.date,
            appointmentTime: dateExtract.time,
            appointmentLocation: event?.location,
            appointmentInfo: `${dateExtract.range} session,`,
            note: {
              text: `${remainingSlots} slot${remainingSlots > 1 ? 's' : ''} available`,
              highlight: participantLength / (event?.maxAttendees - 1) >= 2 / 3,
            },
            appointmentAvatars: Array(participantLength).fill(
              'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
            ),
          },
        ],
        title: dateExtract.month,
        actions: [],
      },
      profileDetails: {
        src: generateImageAssestUrl(user?.profile?.avatar?.contentUrl, 3),
        name: `${user?.profile?.firstname || ''} ${user?.profile?.lastname || ''}`,
        badge: 'Tutor',
        countryCode: user?.country || CountryCode.NG,
        tabs: [{ label: 'Profile', active: false, action: () => openTutorPreview(user) }],
      },
      classDetails: {
        title: event?.name?.find(n => n?.language === defaultLanguage)?.displayName,
        description: <>{event?.description?.find(d => d?.language === defaultLanguage)?.text}</>,
        prices: event?.prices?.map(p => ({
          amount: refinePricesUtil({
            formattedAmount: currencyFormatter.format(
              p?.currencies?.find(c => c?.code === defaultCurrency)?.amount || 0,
            ),
            currency: defaultCurrency,
          }),
          description: p?.description?.find(d => d?.language === defaultLanguage)?.text,
          selected:
            bookings?.groupof3?.price?.id === p?.id || bookings?.groupof6?.price?.id === p?.id,
          discount: '',
          onChange:
            participantLength >= event?.maxAttendees - 1
              ? undefined
              : e =>
                  addBooking({
                    e,
                    price: p,
                    product: event,
                  }),
        })),
      },
    };
  };

  const groupEventsByMaxAttendees = (events: ProductEvent[]): [any[], any[]] => {
    const group4: any[] = [];
    const group7: any[] = [];

    for (const event of events) {
      if (event.maxAttendees === 4) {
        group4.push(reservationDetails(event));
      } else if (event.maxAttendees === 7) {
        group7.push(reservationDetails(event));
      }
    }

    return [group4, group7];
  };

  const allReservation = isProductEventsError
    ? []
    : groupEventsByMaxAttendees(refinedProductEvents || []);

  const handleOrderCreation = async ({
    prices,
    products,
  }: {
    prices: Price[];
    products: ProductEvent[];
  }) => {
    const total = (prices: Price[]) =>
      prices.reduce(
        (acc, curr) => acc + curr?.currencies?.find(c => c?.code === defaultCurrency)?.amount,
        0,
      );

    const createOrderResultData = await createOrder({
      items: await Promise.all(
        products?.map(async p => {
          const startTime = p.startTime?.dateTime;
          const endTime = dayjs(p.startTime?.dateTime).add(3, 'month').toISOString();
          const refinedPrices = prices?.filter(price => price?.productId === p?.id);

          const getProductEventResult = await getProductEvent({
            page: 1,
            limit: 20,
            earliest: startTime,
            latest: endTime,
            recurringId: p.recurringEventId,
          });

          const organizer = p?.organizer;

          const refinedProducts = getProductEventResult?.data
            ?.slice(0, refinedPrices?.[0]?.split)
            ?.map(p => ({
              attendees: p?.attendees?.map(a => ({
                ...a,
                user: undefined,
              })),
              calendarId: p?.calendarId,
              description: p?.description,
              endTime: p?.endTime,
              id: p?.id,
              code: p?.code,
              location: p?.location,
              maxAttendees: p?.maxAttendees,
              category: p?.category,
              name: p?.name,
              startTime: p?.startTime,
              isVirtual: p?.isVirtual,
              organizer,
              recurringEventId: p?.recurringEventId,
            })) as ProductEvent[];

          return {
            shippingMethod: 'SERVICE',
            quantity: 1,
            prices: refinedPrices?.map(
              p =>
                ({
                  currencies: p?.currencies,
                  id: p?.id,
                  productId: p?.productId,
                }) as Price,
            ),
            products: refinedProducts,
            currencyCode: CurrencyCode.NG,
            totalAmount: total(refinedPrices),
            totalAdjustedAmount: 0,
          };
        }),
      ),

      description: [
        {
          language: 'en',
          text: 'Product event order',
        },
      ],
      customer: {
        uid: user?.profile?.ids?.find(p => p?.name === 'uid')?.value,
        email: user?.email,
        name: `${user?.profile?.firstname} ${user?.profile?.lastname}`,
        notification: user?.preferences?.notifications?.find(
          n => n?.medium === NotificationLabel.EMAIL,
        ),
      },
      totalShippingAmount: 0,
      totalAdjustedAmount: 0,
      serviceFee: 0,
      currencyCode: CurrencyCode.NG,
    });

    if ('error' in createOrderResultData) {
      const error = formatError(createOrderResultData.error.message);
      return dispatch(
        updateFlashMessage({
          open: true,
          severity: 'error',
          message: 'An error occurred!',
        }),
      );
    }

    if ('data' in createOrderResultData) {
    }
  };

  const handleCheckout = () => {
    navigate(`/${webPaths.checkout}`);
  };

  useEffect(() => {
    if (myOrders?.length) {
      const items = myOrders?.[0]?.items;
      const groupOf3Item = items?.find(t => t?.products?.some(p => p?.maxAttendees === 4));
      const groupOf6Item = items?.find(t => t?.products?.some(p => p?.maxAttendees === 7));
      const groupOf3Product = groupOf3Item?.products?.[0];
      const groupOf6Product = groupOf6Item?.products?.[0];
      const groupOf3Price = groupOf3Item?.prices?.[0];
      const groupOf6Price = groupOf6Item?.prices?.[0];
      setBookings({
        groupof3: !groupOf3Product
          ? undefined
          : {
              price: groupOf3Price,
              product: groupOf3Product,
            },
        groupof6: !groupOf6Product
          ? undefined
          : {
              price: groupOf6Price,
              product: groupOf6Product,
            },
      });
    }
  }, [myOrders]);

  useEffect(() => {
    setLoadingClient(false);
  }, []);

  return (
    <Container sx={{ pb: { xs: 0.5, md: 0 } }}>
      {!loadingClient && (
        <>
          {previewBooking && (
            <>
              <Box
                sx={{
                  boxShadow: '0px 20px 66px 0px rgba(34, 48, 73, 0.20)',
                  background: globalColours['$global-colour-background-elevated'],
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  maxWidth: '340px',
                  maxHeight: '95dvh',
                  minWidth: { xs: '95dvw', md: '50vw' },
                  minHeight: { xs: '95dvh', md: '50vh' },
                  position: 'fixed',
                  top: '50%',
                  left: '50%',
                  translate: '-50% -50%',
                  zIndex: 70,
                  overflow: 'auto',
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      mb: 1,
                    }}
                  >
                    <Cancel
                      onClick={closePreviewBooking}
                      sx={{
                        width: '24px',
                        height: '24px',
                        color: globalColours['$global-colour-content-primary'],
                        cursor: 'pointer',
                      }}
                    />
                  </Box>

                  <Box sx={{ p: 1 }}>
                    <Reservation {...(reservationDetails(previewBooking) as any)} hideToggle />
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                  }}
                >
                  <Button variant='text' onClick={handleRemoveBooking} sx={{ width: '100%' }}>
                    <Typography variant='body3'>Remove</Typography>
                  </Button>
                  <Button onClick={closePreviewBooking} sx={{ width: '100%' }} disableBottomFixed>
                    Update
                  </Button>
                </Box>
              </Box>
              <Box sx={{ position: 'fixed', inset: 0, zIndex: 60, background: '#000000bb' }}></Box>
            </>
          )}

          {defaultPos.y > 0 && (
            <Draggable
              defaultPosition={defaultPos}
              position={null}
              handle='.drag'
              onStop={toggleMobileSummary}
            >
              <Stack
                className='drag'
                sx={{
                  display: {
                    xs: openMobileSummary || previewBooking ? 'none' : 'flex',
                    md: 'none',
                  },
                  alignItems: 'center',
                  width: 'fit-content',
                  position: 'fixed',
                  zIndex: 99,
                }}
              >
                <Box
                  sx={{
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: globalColours['$global-colour-background-neutral-default'],
                    borderRadius: '50%',
                  }}
                >
                  <Box sx={{ position: 'relative', width: 'fit-content', height: 'fit-content' }}>
                    <BasketIcon
                      sx={{
                        width: '24px',
                        height: '24px',
                        color: globalColours['$global-colour-content-primary'],
                      }}
                    />
                    <Box
                      sx={{
                        width: '9px',
                        height: '9px',
                        background: globalColours['$global-colour-interactive-accent-default'],
                        borderRadius: '50%',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                      }}
                    ></Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    px: 0.25,
                    borderRadius: '8px',
                    background: globalColours['$global-colour-background-neutral-default'],
                    width: 'fit-content',
                  }}
                >
                  <Typography
                    variant='body3'
                    sx={{ color: globalColours['$global-colour-content-secondary'] }}
                  >
                    {refinePricesUtil({
                      formattedAmount: currencyFormatter.format(bookingTotal || 0),
                      currency: defaultCurrency,
                    })}
                  </Typography>
                </Box>
              </Stack>
            </Draggable>
          )}
          <Sheet
            title={'Booking summary'}
            closeSheet={toggleMobileSummary}
            openSheet={openMobileSummary}
            body={
              <>
                {bookings?.groupof3?.product && (
                  <Stack sx={{ gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant='body3'>
                        {
                          bookings?.groupof3?.product?.name?.find(
                            n => n?.language === defaultLanguage,
                          )?.displayName
                        }
                      </Typography>
                      <Typography
                        variant='body3'
                        sx={{
                          width: '74px',
                          color: globalColours['$global-colour-content-secondary'],
                        }}
                      >
                        {refinePricesUtil({
                          formattedAmount: currencyFormatter.format(
                            bookings?.groupof3?.price?.currencies?.find(
                              c => c?.code === defaultCurrency,
                            )?.amount || 0,
                          ),
                          currency: defaultCurrency,
                        })}
                      </Typography>
                    </Box>
                    <Button
                      variant='text'
                      sx={{ p: 0, width: 'fit-content', minWidth: 'fit-content' }}
                      disableBottomFixed
                      onClick={() => handlePreviewBooking(bookings?.groupof3?.product)}
                    >
                      <Typography variant='body3'>Modify</Typography>
                    </Button>
                  </Stack>
                )}
                {!!bookings?.groupof3 && !!bookings?.groupof6 && <Divider sx={{ my: 2 }} />}
                {bookings?.groupof6?.product && (
                  <Stack sx={{ gap: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant='body3'>
                        {
                          bookings?.groupof6?.product?.name?.find(
                            n => n?.language === defaultLanguage,
                          )?.displayName
                        }
                      </Typography>
                      <Typography
                        variant='body3'
                        sx={{
                          width: '74px',
                          color: globalColours['$global-colour-content-secondary'],
                        }}
                      >
                        {refinePricesUtil({
                          formattedAmount: currencyFormatter.format(
                            bookings?.groupof6?.price?.currencies?.find(
                              c => c?.code === defaultCurrency,
                            )?.amount || 0,
                          ),
                          currency: defaultCurrency,
                        })}
                      </Typography>
                    </Box>
                    <Button
                      variant='text'
                      sx={{ p: 0, width: 'fit-content', minWidth: 'fit-content' }}
                      disableBottomFixed
                      onClick={() => handlePreviewBooking(bookings?.groupof6?.product)}
                    >
                      <Typography variant='body3'>Modify</Typography>
                    </Button>
                  </Stack>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant='body2SemiBold'>Total</Typography>
                  <Typography
                    variant='body3'
                    sx={{
                      width: '74px',
                      borderTop: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                      color: globalColours['$global-colour-content-secondary'],
                    }}
                  >
                    {refinePricesUtil({
                      formattedAmount: currencyFormatter.format(bookingTotal || 0),
                      currency: defaultCurrency,
                    })}
                  </Typography>
                </Box>
              </>
            }
          />

          <Sheet
            title={'Tutor Profile'}
            closeSheet={closeTutorPreview}
            openSheet={!!previewTutor}
            body={
              <Box sx={{ px: { xs: 0, md: 3 } }}>
                <UserProfile {...userProfile()} />
              </Box>
            }
          />
        </>
      )}
      <Navigation goBack={goBack} currentStepIndex={0} hideInfo />
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          my: { xs: 3, md: 6 },
          ml: 'auto',
          width: 'fit-content',
          mb: { xs: 8, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: '568px' } }}>
          <Typography
            variant={screens.isMobile ? 'subtitle5' : 'subtitle3'}
            sx={{ textAlign: { md: 'center' }, display: 'block', mx: 'auto' }}
          >
            Join a class
          </Typography>
          <Typography
            variant={screens.isMobile ? 'body3' : 'body2'}
            sx={{
              maxWidth: '500px',
              textAlign: { xs: 'left', md: 'center' },
              display: 'block',
              mb: { xs: 3, md: 4 },
              mx: { xs: 'none', md: 'auto' },
              color: globalColours['$global-colour-content-secondary'],
            }}
          >
            Select one or more from our available group tuition classes.
          </Typography>
          <AlertComp
            sx={{
              display: { xs: 'flex', md: 'none' },
              mb: 3,
            }}
            titleElement={'Payment option'}
            description={
              'You can select one of your parents or guardian to make payment on your behalf at the checkout.'
            }
            sentiment='info'
            icon={
              <PeopleIcon
                sx={theme => ({
                  color: theme?.palette?.common?.white,
                  width: '24px',
                  Height: '24px',
                })}
              />
            }
          />

          <SearchFilters {...searchFiltersDetails} />
          <Stack sx={{ gap: 6, mt: { xs: 2, md: 3 } }}>
            {loadingProductEvents || fetchingProductEvents || loadingMyOrders ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                {allReservation.map((reservations, i) => (
                  <Fragment key={i}>
                    <Stack sx={{ gap: 2 }}>
                      {reservations.map((reservation, index) => (
                        <Fragment key={index}>
                          <Reservation {...reservation} />
                        </Fragment>
                      ))}
                    </Stack>
                  </Fragment>
                ))}
              </>
            )}
          </Stack>

          <Button
            sx={{ mt: 2, display: { xs: 'flex', md: 'none' } }}
            showRightIcon
            onClick={handleCheckout}
          >
            Checkout
          </Button>
        </Box>
        <Box sx={{ maxWidth: '250px', display: { xs: 'none', md: 'block' } }}>
          <AlertComp
            titleElement={'Payment option'}
            description={
              'You can select one of your parents or guardian to make payment on your behalf at the checkout.'
            }
            sentiment='info'
            icon={
              <PeopleIcon
                sx={theme => ({
                  color: theme?.palette?.common?.white,
                  width: '24px',
                  Height: '24px',
                })}
              />
            }
          />
          {(bookings?.groupof3 || bookings?.groupof6) && (
            <>
              <Typography variant='subtitle5' sx={{ mt: 3, display: 'block' }}>
                Booking summary
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {bookings?.groupof3?.product && (
                <Stack sx={{ gap: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant='body3'>
                      {
                        bookings?.groupof3?.product?.name?.find(
                          n => n?.language === defaultLanguage,
                        )?.displayName
                      }
                    </Typography>
                    <Typography
                      variant='body3'
                      sx={{
                        width: '74px',
                        color: globalColours['$global-colour-content-secondary'],
                      }}
                    >
                      {refinePricesUtil({
                        formattedAmount: currencyFormatter.format(
                          bookings?.groupof3?.price?.currencies?.find(
                            c => c?.code === defaultCurrency,
                          )?.amount || 0,
                        ),
                        currency: defaultCurrency,
                      })}
                    </Typography>
                  </Box>
                  <Button
                    variant='text'
                    sx={{ p: 0, width: 'fit-content', minWidth: 'fit-content' }}
                    disableBottomFixed
                    onClick={() => handlePreviewBooking(bookings?.groupof3?.product)}
                  >
                    <Typography variant='body3'>Modify</Typography>
                  </Button>
                </Stack>
              )}
              {!!bookings?.groupof3 && !!bookings?.groupof6 && <Divider sx={{ my: 2 }} />}
              {bookings?.groupof6?.product && (
                <Stack sx={{ gap: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant='body3'>
                      {
                        bookings?.groupof6?.product?.name?.find(
                          n => n?.language === defaultLanguage,
                        )?.displayName
                      }
                    </Typography>
                    <Typography
                      variant='body3'
                      sx={{
                        width: '74px',
                        color: globalColours['$global-colour-content-secondary'],
                      }}
                    >
                      {refinePricesUtil({
                        formattedAmount: currencyFormatter.format(
                          bookings?.groupof6?.price?.currencies?.find(
                            c => c?.code === defaultCurrency,
                          )?.amount || 0,
                        ),
                        currency: defaultCurrency,
                      })}
                    </Typography>
                  </Box>
                  <Button
                    variant='text'
                    sx={{ p: 0, width: 'fit-content', minWidth: 'fit-content' }}
                    disableBottomFixed
                    onClick={() => handlePreviewBooking(bookings?.groupof6?.product)}
                  >
                    <Typography variant='body3'>Modify</Typography>
                  </Button>
                </Stack>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography variant='body2SemiBold'>Total</Typography>
                <Typography
                  variant='body3'
                  sx={{
                    width: '74px',
                    borderTop: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
                    color: globalColours['$global-colour-content-secondary'],
                  }}
                >
                  {refinePricesUtil({
                    formattedAmount: currencyFormatter.format(bookingTotal || 0),
                    currency: defaultCurrency,
                  })}
                </Typography>
              </Box>

              <Button
                sx={{ mt: 2, width: '100% !important' }}
                proceedingIcon={
                  <ArrowLeftIcon
                    sx={{ width: '16px', height: '16px', transform: 'rotate(180deg)' }}
                  />
                }
                onClick={handleCheckout}
              >
                Checkout
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

BookTuition.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  nextScreen: PropTypes.func,
};

BookTuition.defaultProps = {
  children: null,
  className: null,
  nextScreen: always,
};

export default styled<any>(BookTuition)(
  ({ theme }) => `
  `,
);
